/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the master's data life.

   LIVE SHARED DRAFT (docs/SPEC_WORKFLOW.md §5, plan A2): a master's draft
   is ONE shared Yjs document (`master:<id>`) on the /collab socket, edited
   live by everyone with edit rights. No checkout, no lock, no 30-second
   timer — every keystroke syncs, the server persists the document, and
   the chip says Saved / Saving… / Offline.

   The Yjs document holds three things:
     · `content` (XmlFragment) — the Office Master text the canvas binds to;
     · `layers`  (Map: layerId → { pos, layer }) — the client variants, so
       the variant bar sees everyone's layer edits;
     · `meta`    (Map) — `seeded` (the doc was filled from the JSON draft /
       published master), `edited` (the draft differs from published — a
       draft row exists), `seeder` (set by the SERVER: the one seat allowed
       to seed, so two people opening at once never paste the master twice).

   JSON MIRROR: every existing reader (library list, review queue, publish,
   History) reads `spec_master_drafts.core_content / layers`. A local edit
   debounces 1.5s, then the blocks + layers are written there through the
   SAME SpecService.saveDraft path as before. Save / Save As / Discard /
   Publish / Submit-for-review / Revert / Rename keep their behaviour.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import * as Y from 'yjs';
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import type { ClientLayer, ContentBlock, SpecMaster, SpecMasterDraft } from '../../shared/types';
import { SpecService, cleanSaveError } from '../services/specService';
import { reportError } from '../services/errorLog';
import { pushEditHistory } from '../../shared/editHistory';
import { docToBlocks } from '../designer/schema';
import { useCollabDoc } from './useCollabDoc';
import { COLLAB_FIELD, type SpecCanvasCollab } from './collabBinding';
import { SEED_ORIGIN, fillFragment, isDocSeeded, seedDoc } from './collabSeed';
import {
  buildPublishResult,
  planPublish,
  type PublishAnswers,
  type PublishPlan,
} from './publishPlan';
import {
  collabDocName,
  deriveStudioChipState,
  draftExistsNow,
  draftReloadPlan,
  mirrorWriteAllowed,
  showDraftInProgress,
  type DraftReloadCause,
  layerDecisionTargets,
  isDraftEndedError,
  diffStoredLayers,
  orderStoredLayers,
  sharedDraftId,
  shouldSeed,
  type CollabPeer,
  type LiveBlock,
  type StudioChipState,
  type StoredLayer,
} from './collabState';

/** Quiet time after a local edit before the JSON mirror is written. */
export const MIRROR_MS = 1500;

/** The server's exact 409 body for a duplicate section number
 *  (ec2-api/server.js) — the plan requires this shown verbatim in the
 *  toast, never behind a "Save failed:" / "Save As failed:" prefix. */
const SECTION_NUMBER_CONFLICT = 'That section number is already in use';

/** `cleanSaveError()` strips the cloudCache wrapper; this adds the toast's
 *  action prefix EXCEPT for the section-number conflict message. */
function saveErrorToast(prefix: string, err: unknown): string {
  const msg = cleanSaveError(err);
  return msg === SECTION_NUMBER_CONFLICT ? msg : prefix + msg;
}

/** Transaction origins this hook writes with. Anything else that is not the
 *  provider (a remote update) is a LOCAL user edit — the canvas's sync. */
const LAYER_EDIT_ORIGIN = 'sf-layers';
/** A layer change already persisted elsewhere (an accepted update): shared,
 *  but not an edit. */
const LAYER_SYNC_ORIGIN = 'sf-layers-sync';
const META_ORIGIN = 'sf-meta';

type LayerMap = Y.Map<StoredLayer<ClientLayer>>;

function writeLayers(map: LayerMap, next: ClientLayer[]): void {
  const current = new Map<string, StoredLayer<ClientLayer>>(map.entries());
  const { remove, set } = diffStoredLayers(current, next);
  for (const id of remove) map.delete(id);
  for (const [id, entry] of set) map.set(id, entry);
}

export type LoadState = 'loading' | 'ready' | 'not-found' | 'error';

/** SPEC_WORKFLOW §4: View = the PUBLISHED text, read-only; Edit = the shared
 *  draft. Mode is state in the Studio, never a route. */
export type StudioMode = 'view' | 'edit';

/** What the Publish button resolves to. */
export type PublishStart =
  | { kind: 'nothing' }
  | { kind: 'review' }
  | { kind: 'plan'; plan: PublishPlan };

const NO_BLOCKS: ContentBlock[] = [];
const NO_LAYERS: ClientLayer[] = [];

export interface SpecMasterDraftApi {
  loadState: LoadState;
  error: string | null;
  master: SpecMaster | null;
  draft: SpecMasterDraft | null;
  /** The blocks the canvas loads when it is NOT bound to the shared
   *  document (a variant tab, or before the first sync). Identity changes
   *  ONLY on a real reload (open, discard, revert). */
  initialBlocks: ContentBlock[];
  /** Client layers on this master (Masters Ladder R3) — shared live. */
  layers: ClientLayer[];
  /** Replace the layer set — an edit, exactly like typing.
   *  `{ dirty: false }` is for a layer change that is ALREADY persisted
   *  elsewhere (Phase 3: the server applies an accepted update to
   *  spec_masters.layers itself) — shared with everyone, but it does not
   *  start a draft on a master nobody was editing. */
  setLayers: (next: ClientLayer[], opts?: { dirty?: boolean }) => void;
  /** An offer was answered (the server already rewrote the published
   *  master's layer): apply the same pure transform to the published layers
   *  AND, independently, to the draft's shared layers. Works in View and Edit. */
  applyLayerDecision: (layerId: string, apply: (layer: ClientLayer) => ClientLayer) => void;
  /** The layers as they stand right now, for callers that run outside React's
   *  render cycle (the canvas's change handler). */
  currentLayers: () => ClientLayer[];
  /** The draft differs from published (a shared draft exists), or a rename
   *  is pending. */
  dirty: boolean;
  /** Work that is NOT on the server yet — unsent edits, the JSON mirror in
   *  flight, a pending rename. What the exit guard protects. */
  unsaved: boolean;
  /** Legacy flag: `saveState === 'saving'`. */
  saving: boolean;
  /** The one chip: Connecting… / Saved / Saving… / Offline. */
  saveState: StudioChipState;
  /** The server refuses live editing in a way no retry fixes (auth, origin,
   *  bad request, too many, denied) — null once a reconnect syncs. */
  liveBlocked: LiveBlock | null;
  /** Everyone else in this master right now. */
  peers: CollabPeer[];
  /** The shared document to bind the canvas to — null until it has synced
   *  and been seeded (until then the canvas shows `initialBlocks`). */
  collab: SpecCanvasCollab | null;
  /** Someone ELSE discarded or published the draft — the socket is closed
   *  and the Studio should refresh. */
  discardedElsewhere: boolean;
  /** A shared draft exists (a draft row, or the live doc says it was edited)
   *  — what View mode's "Draft in progress" line keys on. */
  draftExists: boolean;
  /** View mode's "Draft in progress" line: a draft row exists, or people
   *  are in the live doc — false once the draft ended. */
  draftInProgress: boolean;
  /** The draft's content was changed outside the editor (e.g. an accepted
   *  module update) - the socket is closed; the Studio should say so and
   *  refresh so it continues from the fresh content. */
  resetElsewhere: boolean;
  /** Where this draft sits in the review workflow, or null. */
  prStatus: 'pending' | 'approved' | 'rejected' | null;

  /** The editor's current document, handed over on every change. */
  onBlocksChanged: (blocks: ContentBlock[]) => void;
  /** The blocks as they stand right now (for Save As / publish paths). */
  currentBlocks: () => ContentBlock[];

  /** Flush the JSON mirror (and a pending rename) now. Resolves false when
   *  the flush failed (already toasted) — callers must not leave then. */
  save: () => Promise<boolean>;
  saveAs: (title: string, csiNumber: string) => Promise<SpecMaster | null>;
  /** Master spec rename (D4, click-to-rename crumb): patches `title` and/or
   *  `csiNumber` on `master`. Trims; an empty value after trim is refused
   *  (no-op) rather than clearing the field. Persists metadata-only
   *  (title/csiNumber) on the next `save()`, straight to the published
   *  master via `SpecService.savePublished` — no version bump, no
   *  publish/review cycle. Uniqueness on `csiNumber` is the caller's job
   *  (`csiConflict`) BEFORE calling this. */
  rename: (patch: { title?: string; csiNumber?: string }) => void;
  discard: () => Promise<boolean>;
  /** Flush the draft and work out what the publish means: nothing to
   *  publish, the review route (no specs.publish), or the strict plan the
   *  publish dialog walks. */
  publish: (canDirectPublish: boolean) => Promise<PublishStart>;
  /** The ONE publish write (a server batch). On success the Studio is left
   *  in View of the new version. Throws the server's error untouched. */
  commitPublish: (plan: PublishPlan, answers: PublishAnswers) => Promise<SpecMaster>;
  /** Re-plan the publish from the LIVE draft right now (the stale-dialog
   *  guard: a teammate may have edited while the dialog was open). */
  replanPublish: () => Promise<PublishPlan | null>;
  submitForReview: (message: string) => Promise<void>;
  revert: (version: number) => Promise<void>;
  reload: () => Promise<void>;
  /** Re-read the master + draft and reconnect the shared doc WITHOUT the
   *  loading state (after a publish, or a teammate ending the draft). A
   *  teammate's end / a reset re-reads from the SERVER (draftReloadPlan). */
  refresh: (cause?: DraftReloadCause, known?: SpecMaster) => Promise<void>;
}

export function useSpecMasterDraft(
  masterId: string,
  userName: string,
  toast: (msg: string, isError?: boolean) => void,
  opts?: { canEdit?: boolean; mode?: StudioMode }
): SpecMasterDraftApi {
  const canEdit = opts?.canEdit !== false;
  const mode: StudioMode = opts?.mode ?? 'edit';
  const modeRef = React.useRef<StudioMode>(mode);
  modeRef.current = mode;
  const [epoch, setEpoch] = React.useState(0);
  const [loadState, setLoadState] = React.useState<LoadState>('loading');
  const [error, setError] = React.useState<string | null>(null);
  const [master, setMaster] = React.useState<SpecMaster | null>(null);
  const [draft, setDraft] = React.useState<SpecMasterDraft | null>(null);
  const [initialBlocks, setInitialBlocks] = React.useState<ContentBlock[]>([]);
  const [layers, setLayersState] = React.useState<ClientLayer[]>([]);
  const [edited, setEdited] = React.useState(false);
  const [seeded, setSeeded] = React.useState(false);
  const [seeder, setSeeder] = React.useState<string | null>(null);
  const [renamePending, setRenamePending] = React.useState(false);
  const [mirrorPending, setMirrorPending] = React.useState(false);

  const blocksRef = React.useRef<ContentBlock[]>([]);
  /** The shared fragment changed since `blocksRef` was last derived. */
  const blocksStaleRef = React.useRef(false);
  const layersRef = React.useRef<ClientLayer[]>([]);
  const masterRef = React.useRef<SpecMaster | null>(null);
  const draftRef = React.useRef<SpecMasterDraft | null>(null);
  /** Pending title/csiNumber patch from `rename()`, not yet persisted.
   *  Cleared once `save()` writes it to the published master. */
  const renamePatchRef = React.useRef<{ title?: string; csiNumber?: string } | null>(null);
  /** This window is the one ending the draft (discard / publish) — the 4409
   *  that follows is not news to it. */
  const selfEndingRef = React.useRef(false);
  /** The draft has ENDED for this connection (our own discard/publish, or a
   *  4409) — no mirror write may recreate its row (mirrorWriteAllowed). */
  const endedRef = React.useRef(false);
  /** The server answered a mirror write with 409 draft-ended. */
  const [endedRemotely, setEndedRemotely] = React.useState(false);
  /** The Y.Doc this window ended itself — its 4409 is not news. */
  const selfEndedDocRef = React.useRef<Y.Doc | null>(null);
  const toastRef = React.useRef(toast);
  toastRef.current = toast;
  masterRef.current = master;
  draftRef.current = draft;

  // ─── The shared document ───
  const docName = loadState === 'ready' ? collabDocName('master', masterId) : null;
  const collabDoc = useCollabDoc(docName, userName, { announce: mode === 'edit', epoch });
  const ydoc = collabDoc?.ydoc ?? null;
  const provider = collabDoc?.provider ?? null;
  const ydocRef = React.useRef<Y.Doc | null>(null);
  ydocRef.current = ydoc;
  const seededRef = React.useRef(false);
  seededRef.current = seeded;
  const editedRef = React.useRef(false);
  editedRef.current = edited;

  const currentBlocks = React.useCallback(() => {
    const d = ydocRef.current;
    if (d && seededRef.current && blocksStaleRef.current) {
      const json = yXmlFragmentToProsemirrorJSON(d.getXmlFragment(COLLAB_FIELD));
      blocksRef.current = docToBlocks(json);
      blocksStaleRef.current = false;
    }
    return blocksRef.current;
  }, []);

  /** Every write goes through here, so `coreContent` and `layers` can never
   *  drift apart mid-save (the variant bar and the canvas both feed refs). */
  const withContent = React.useCallback(
    (d: SpecMasterDraft): SpecMasterDraft => ({
      ...d,
      coreContent: currentBlocks(),
      layers: layersRef.current,
    }),
    [currentBlocks]
  );

  /** Writes a pending `rename()` patch straight to the published master —
   *  title/csiNumber are metadata, not draft content, so this persists
   *  ahead of (or instead of) a draft save (no version bump, no
   *  publish/review cycle). A no-op when nothing is pending. Shared by
   *  `save()`, `publish()`, `submitForReview()` and the leave-page flush so
   *  a rename survives every exit from the draft, not just Save. */
  const flushRename = React.useCallback(async () => {
    const pendingRename = renamePatchRef.current;
    const m = masterRef.current;
    if (!pendingRename || !m) return;
    // Re-fetch first so we never clobber another field written concurrently —
    // overlay ONLY the rename patch onto the server's current row.
    const fresh = (await SpecService.getMaster(m.id)) || m;
    const merged = {
      ...fresh,
      ...pendingRename,
      modifiedBy: userName,
      editHistory: pushEditHistory(fresh.editHistory, userName, undefined, 'edited'),
    };
    const savedMaster = await SpecService.savePublished(merged);
    setMaster(savedMaster);
    masterRef.current = savedMaster;
    renamePatchRef.current = null;
    setRenamePending(false);
  }, [userName]);

  const flushRenameRef = React.useRef(flushRename);
  flushRenameRef.current = flushRename;

  /** Take a freshly read master + shared draft as the starting point. */
  const applyLoaded = React.useCallback((m: SpecMaster, existing: SpecMasterDraft | null) => {
    setMaster(m);
    masterRef.current = m;
    // A draft from before migration 044 has no `layers` key at all — fall
    // back to the published master's so a variant is never lost.
    const startLayers = (existing?.layers ?? m.layers ?? []) as ClientLayer[];
    layersRef.current = startLayers;
    setLayersState(startLayers);
    setDraft(existing);
    draftRef.current = existing;
    const rows = (existing ? existing.coreContent : m.coreContent) || [];
    setInitialBlocks(rows);
    blocksRef.current = rows;
    blocksStaleRef.current = false;
    editedRef.current = !!existing;
    setEdited(!!existing);
    seededRef.current = false;
    setSeeded(false);
    setSeeder(null);
    setMirrorPending(false);
    setEndedRemotely(false);
  }, []);

  // ─── Load ───
  const load = React.useCallback(async () => {
    setLoadState('loading');
    setError(null);
    try {
      const m = await SpecService.getMaster(masterId);
      if (!m) {
        setLoadState('not-found');
        return;
      }
      // The SHARED draft — whoever started it.
      const existing = await SpecService.getSharedDraft(m.id);
      applyLoaded(m, existing);
      setLoadState('ready');
    } catch (err) {
      reportError('SpecStudio:load', err);
      setError(err instanceof Error ? err.message : 'Could not open this spec master.');
      setLoadState('error');
    }
  }, [masterId, applyLoaded]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // ─── JSON mirror ───
  const mirrorTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mirrorInFlight = React.useRef<Promise<SpecMasterDraft | null> | null>(null);
  const mirrorAgain = React.useRef(false);

  /** Mark the shared draft as differing from published (once). */
  const markEdited = React.useCallback(() => {
    const d = ydocRef.current;
    if (!d || editedRef.current) return;
    editedRef.current = true;
    setEdited(true);
    d.transact(() => d.getMap('meta').set('edited', true), META_ORIGIN);
  }, []);

  /** Does the document now differ from what is published? */
  const differsFromPublished = React.useCallback((): boolean => {
    const m = masterRef.current;
    if (!m) return true;
    return (
      JSON.stringify(currentBlocks()) !== JSON.stringify(m.coreContent || []) ||
      JSON.stringify(layersRef.current) !== JSON.stringify(m.layers || [])
    );
  }, [currentBlocks]);

  /** Write blocks + layers to the draft row now. Creates the row the first
   *  time (deterministic shared id — the collab server's store lands on the
   *  same row). Serialized: a change during a write queues one more. */
  const writeMirror = React.useCallback(async (): Promise<SpecMasterDraft | null> => {
    if (mirrorInFlight.current) {
      mirrorAgain.current = true;
      return mirrorInFlight.current;
    }
    const run = async (): Promise<SpecMasterDraft | null> => {
      const m = masterRef.current;
      if (!m) return null;
      // A discarded / published draft stays gone — never recreate its row.
      if (!mirrorWriteAllowed({ ended: endedRef.current, docDiscarded: false })) return null;
      if (!editedRef.current) {
        if (!differsFromPublished()) return draftRef.current;
        markEdited();
      }
      let d = draftRef.current;
      if (!d) d = await SpecService.getSharedDraft(m.id);
      if (!d) d = SpecService.newSharedDraft(m, sharedDraftId('master', m.id), userName);
      let saved: SpecMasterDraft;
      try {
        saved = await SpecService.saveDraft(withContent(d), { collabMirror: true });
      } catch (err) {
        // The draft ended under us (a publish/discard raced this write): the
        // same as a 4409 — stop, no toast, the Studio refreshes.
        if (isDraftEndedError(err)) {
          endedRef.current = true;
          setEndedRemotely(true);
          return null;
        }
        throw err;
      }
      draftRef.current = saved;
      setDraft(saved);
      return saved;
    };
    const p = (async () => {
      try {
        let out = await run();
        while (mirrorAgain.current) {
          mirrorAgain.current = false;
          out = await run();
        }
        return out;
      } finally {
        mirrorInFlight.current = null;
      }
    })();
    mirrorInFlight.current = p;
    return p;
  }, [differsFromPublished, markEdited, userName, withContent]);

  const flushMirror = React.useCallback(async (): Promise<SpecMasterDraft | null> => {
    if (mirrorTimer.current) {
      clearTimeout(mirrorTimer.current);
      mirrorTimer.current = null;
    }
    try {
      return await writeMirror();
    } finally {
      setMirrorPending(false);
    }
  }, [writeMirror]);

  const cancelMirror = React.useCallback(() => {
    if (mirrorTimer.current) {
      clearTimeout(mirrorTimer.current);
      mirrorTimer.current = null;
    }
    mirrorAgain.current = false;
    setMirrorPending(false);
  }, []);

  const scheduleMirror = React.useCallback(() => {
    if (!mirrorWriteAllowed({ ended: endedRef.current, docDiscarded: false })) return;
    setMirrorPending(true);
    if (mirrorTimer.current) clearTimeout(mirrorTimer.current);
    mirrorTimer.current = setTimeout(() => {
      mirrorTimer.current = null;
      void flushMirror().catch((err) => {
        reportError('SpecStudio:mirror', err);
        toastRef.current(saveErrorToast('Could not save the draft: ', err), true);
      });
    }, MIRROR_MS);
  }, [flushMirror]);

  // ─── Observe the shared document ───
  React.useEffect(() => {
    if (!ydoc || !provider) return;
    const fragment = ydoc.getXmlFragment(COLLAB_FIELD);
    const layerMap: LayerMap = ydoc.getMap<StoredLayer<ClientLayer>>('layers');
    const meta = ydoc.getMap<unknown>('meta');

    const readMeta = () => {
      if (meta.get('edited') === true) {
        editedRef.current = true;
        setEdited(true);
      }
      const s = meta.get('seeder');
      setSeeder(typeof s === 'string' ? s : null);
      if (meta.get('seeded') === true || fragment.length > 0) setSeeded(true);
    };
    const readLayers = () => {
      if (!seededRef.current && meta.get('seeded') !== true) return;
      const next = orderStoredLayers<ClientLayer>(layerMap.entries());
      layersRef.current = next;
      setLayersState(next);
    };
    const onFragment = () => {
      blocksStaleRef.current = true;
    };
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      // Remote (the provider applied it), seeding, a layer sync, or our own
      // meta flag — none of those is this user editing.
      if (
        origin === provider ||
        origin === SEED_ORIGIN ||
        origin === LAYER_SYNC_ORIGIN ||
        origin === META_ORIGIN
      ) {
        return;
      }
      if (!seededRef.current) return;
      scheduleMirror();
    };

    meta.observe(readMeta);
    layerMap.observe(readLayers);
    fragment.observeDeep(onFragment);
    ydoc.on('update', onUpdate);
    readMeta();
    return () => {
      meta.unobserve(readMeta);
      layerMap.unobserve(readLayers);
      fragment.unobserveDeep(onFragment);
      ydoc.off('update', onUpdate);
    };
  }, [ydoc, provider, scheduleMirror]);

  // ─── Seed (exactly one writer, once) ───
  const synced = !!collabDoc?.synced;
  const seat = collabDoc?.seat ?? '';
  React.useEffect(() => {
    if (!ydoc || !synced) return;
    const fragment = ydoc.getXmlFragment(COLLAB_FIELD);
    const meta = ydoc.getMap<unknown>('meta');
    const isSeeded = isDocSeeded(ydoc);
    if (isSeeded) {
      if (!seededRef.current) {
        seededRef.current = true;
        setSeeded(true);
        blocksStaleRef.current = true;
        const stored = orderStoredLayers<ClientLayer>(
          ydoc.getMap<StoredLayer<ClientLayer>>('layers').entries()
        );
        if (stored.length || meta.get('seeded') === true) {
          layersRef.current = stored;
          setLayersState(stored);
        }
      }
      return;
    }
    if (
      !shouldSeed({
        synced,
        readOnly: !canEdit,
        fragmentEmpty: fragment.length === 0,
        seeded: false,
        seeder,
        mySeat: seat,
      })
    ) {
      return;
    }
    const d = draftRef.current;
    const m = masterRef.current;
    if (!m) return;
    const blocks = (d ? d.coreContent : m.coreContent) || [];
    const startLayers = (d?.layers ?? m.layers ?? []) as ClientLayer[];
    // ONE transaction: content, layers and the flags land together, so a
    // peer never sees a half-seeded document.
    seedDoc(ydoc, blocks, () => {
      writeLayers(ydoc.getMap<StoredLayer<ClientLayer>>('layers'), startLayers);
      if (d) meta.set('edited', true);
    });
    seededRef.current = true;
    setSeeded(true);
    blocksRef.current = blocks;
    blocksStaleRef.current = false;
    layersRef.current = startLayers;
    setLayersState(startLayers);
  }, [ydoc, synced, seeder, seat, canEdit]);

  // The content changed outside the editor: a pending mirror would write the
  // OLD document over the fresh JSON, so drop it (the Studio then leaves).
  const resetDoc = !!collabDoc?.reset;
  React.useEffect(() => {
    if (!resetDoc) return;
    if (mirrorTimer.current) {
      clearTimeout(mirrorTimer.current);
      mirrorTimer.current = null;
    }
    setMirrorPending(false);
  }, [resetDoc]);

  // Leaving mid-burst must not lose the burst.
  React.useEffect(
    () => () => {
      if (renamePatchRef.current) {
        void flushRename().catch((err) => reportError('SpecStudio:leave-rename', err));
      }
      if (mirrorTimer.current && !selfEndingRef.current && !endedRef.current) {
        void flushMirror().catch((err) => reportError('SpecStudio:leave-mirror', err));
      }
    },
    [flushRename, flushMirror]
  );

  // A 4409 (a teammate discarded or published): the draft is over for this
  // connection — drop the pending mirror write so it cannot resurrect it.
  const docDiscarded = !!collabDoc?.discarded;
  React.useEffect(() => {
    if (!docDiscarded) return;
    endedRef.current = true;
    cancelMirror();
  }, [docDiscarded, cancelMirror]);

  const onBlocksChanged = React.useCallback((blocks: ContentBlock[]) => {
    // Bound to the shared document: the canvas's blocks ARE the fragment's
    // (local and remote changes both arrive here). The mirror is driven by
    // the Y.Doc's own local updates, not by this callback.
    blocksRef.current = blocks;
    blocksStaleRef.current = false;
  }, []);

  const setLayers = React.useCallback((next: ClientLayer[], o?: { dirty?: boolean }) => {
    layersRef.current = next;
    setLayersState(next);
    const d = ydocRef.current;
    if (d && seededRef.current) {
      d.transact(
        () => writeLayers(d.getMap<StoredLayer<ClientLayer>>('layers'), next),
        o?.dirty === false ? LAYER_SYNC_ORIGIN : LAYER_EDIT_ORIGIN
      );
    }
  }, []);

  // ─── Rename (T2.1) — title / section number on the crumb ───
  const rename = React.useCallback((patch: { title?: string; csiNumber?: string }) => {
    const m = masterRef.current;
    if (!m) return;
    const next: Partial<SpecMaster> = {};
    if (patch.title !== undefined) {
      const v = patch.title.trim();
      if (!v) return; // refuse empty
      next.title = v;
    }
    if (patch.csiNumber !== undefined) {
      const v = patch.csiNumber.trim();
      if (!v) return; // refuse empty
      next.csiNumber = v;
    }
    if (!Object.keys(next).length) return;
    const updated = { ...m, ...next };
    setMaster(updated);
    masterRef.current = updated;
    renamePatchRef.current = { ...renamePatchRef.current, ...next };
    setRenamePending(true);
    // No Save button any more (SPEC_WORKFLOW §5): a rename lands right away.
    void flushRenameRef.current().catch((err: unknown) => {
      reportError('SpecStudio:rename', err);
      toastRef.current(saveErrorToast('Rename failed: ', err), true);
    });
  }, []);

  // ─── Save (T9) — flush now; edits already sync live ───
  const save = React.useCallback(async () => {
    const pendingRename = renamePatchRef.current;
    // Ctrl+S is a SILENT flush now — the chip already says Saved / Saving….
    if (!editedRef.current && !pendingRename && !(seededRef.current && differsFromPublished())) {
      return true;
    }
    try {
      await flushRename();
      if (seededRef.current && modeRef.current === 'edit') await flushMirror();
      return true;
    } catch (err) {
      reportError('SpecStudio:save', err);
      toastRef.current(saveErrorToast('Save failed: ', err), true);
      return false;
    }
  }, [flushRename, flushMirror, differsFromPublished]);

  // ─── Save As (T7) ───
  const saveAs = React.useCallback(
    async (title: string, csiNumber: string): Promise<SpecMaster | null> => {
      const m = masterRef.current;
      if (!m || !title.trim() || !csiNumber.trim()) return null;
      try {
        const saved = await SpecService.saveAsNewMaster(
          {
            ...m,
            csiNumber: csiNumber.trim(),
            coreContent: currentBlocks(),
            layers: layersRef.current,
          },
          title.trim(),
          userName
        );
        toastRef.current(`Saved as "${saved.title}" — v1`);
        return saved;
      } catch (err) {
        reportError('SpecStudio:saveAs', err);
        toastRef.current(saveErrorToast('Save As failed: ', err), true);
        return null;
      }
    },
    [userName, currentBlocks]
  );

  // ─── Discard (T8) — the shared draft, for everyone ───
  const discard = React.useCallback(async (): Promise<boolean> => {
    const hadPendingRename = !!renamePatchRef.current;
    const m = masterRef.current;
    let d = draftRef.current;
    try {
      if (!d && m) d = await SpecService.getSharedDraft(m.id);
    } catch (err) {
      // The server could not be asked — say so; never a silent no-op.
      reportError('SpecStudio:discard', err);
      toastRef.current(
        'Discard failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
        true
      );
      return false;
    }
    if (!d && !hadPendingRename && !editedRef.current) {
      toastRef.current('There is no draft to discard');
      return false;
    }
    try {
      selfEndingRef.current = true;
      selfEndedDocRef.current = ydocRef.current;
      if (mirrorTimer.current) {
        clearTimeout(mirrorTimer.current);
        mirrorTimer.current = null;
      }
      // An edit the mirror had not written yet still has a row coming (the
      // collab server's store) — write it now so the delete has a row to hit.
      if (!d && editedRef.current) d = await writeMirror();
      // From here on nothing may write the row back (a late mirror would
      // resurrect the draft for everyone).
      endedRef.current = true;
      cancelMirror();
      // EVERY draft row for this master — the shared one and any older
      // per-user drafts — so none resurfaces after the discard. Deleting a
      // row is what evicts the shared document: every peer gets 4409.
      const ids = m ? await SpecService.draftIdsFor(m.id) : [];
      if (d && !ids.includes(d.id)) ids.push(d.id);
      for (const id of ids) await SpecService.discardDraft(id);
      if (hadPendingRename) {
        // A rename made in this session but never persisted — drop the
        // patch and restore the published title/number so the crumb shows
        // the truth and a later Save can't write the discarded rename.
        renamePatchRef.current = null;
        setRenamePending(false);
        if (m) {
          const fresh = await SpecService.getMaster(m.id);
          if (fresh) {
            setMaster(fresh);
            masterRef.current = fresh;
          }
        }
      }
      editedRef.current = false;
      setEdited(false);
      draftRef.current = null;
      setDraft(null);
      toastRef.current('Draft discarded');
      return true;
    } catch (err) {
      selfEndingRef.current = false;
      selfEndedDocRef.current = null;
      endedRef.current = false;
      reportError('SpecStudio:discard', err);
      toastRef.current(
        'Discard failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
        true
      );
      return false;
    }
  }, [writeMirror, cancelMirror]);

  // ─── Refresh — the new published truth, without the loading state ───
  const refresh = React.useCallback(
    async (cause: DraftReloadCause = 'ended-elsewhere', known?: SpecMaster) => {
      try {
        if (draftReloadPlan(cause).refetch) SpecService.forgetCachedMasters();
        const m = known ?? (await SpecService.getMaster(masterId));
        if (!m) {
          setLoadState('not-found');
          return;
        }
        const existing = await SpecService.getSharedDraft(m.id);
        endedRef.current = false;
        selfEndingRef.current = false;
        applyLoaded(m, existing);
        // A fresh socket: the old document was evicted with its row.
        setEpoch((e) => e + 1);
      } catch (err) {
        reportError('SpecStudio:refresh', err);
        toastRef.current(
          'Could not refresh this spec master: ' +
            (err instanceof Error ? err.message : 'Unknown error'),
          true
        );
      }
    },
    [masterId, applyLoaded]
  );

  // ─── Publish (SPEC_WORKFLOW §7) / Submit for review (T10) ───
  /** The plan from the LIVE draft: flush the mirror, read the published
   *  master fresh, and measure the live blocks + layers against it. */
  const replanPublish = React.useCallback(async (): Promise<PublishPlan | null> => {
    const saved = (await flushMirror()) ?? draftRef.current;
    if (!saved) return null;
    const fresh = (await SpecService.getMaster(masterId)) || masterRef.current;
    if (!fresh) throw new Error('This spec master could not be found');
    // A pending rename is master metadata — it rides the publish batch
    // instead of a separate write.
    const base: SpecMaster = { ...fresh, ...(renamePatchRef.current || {}) };
    return planPublish({ master: base, draft: withContent(saved), by: userName });
  }, [masterId, userName, flushMirror, withContent]);

  const publish = React.useCallback(
    async (canDirectPublish: boolean): Promise<PublishStart> => {
      if (
        !draftRef.current &&
        !editedRef.current &&
        !(seededRef.current && differsFromPublished())
      ) {
        toastRef.current('Make an edit before publishing');
        return { kind: 'nothing' };
      }
      if (!canDirectPublish) {
        await flushRename();
        const forReview = (await flushMirror()) ?? draftRef.current;
        if (!forReview) {
          toastRef.current('Make an edit before publishing');
          return { kind: 'nothing' };
        }
        return { kind: 'review' };
      }
      const plan = await replanPublish();
      if (!plan) {
        toastRef.current('Make an edit before publishing');
        return { kind: 'nothing' };
      }
      return { kind: 'plan', plan };
    },
    [flushRename, flushMirror, differsFromPublished, replanPublish]
  );

  const commitPublish = React.useCallback(
    async (plan: PublishPlan, answers: PublishAnswers): Promise<SpecMaster> => {
      const result = buildPublishResult(plan, answers, { by: userName });
      const ids = await SpecService.draftIdsFor(plan.master.id);
      if (!ids.includes(plan.draft.id)) ids.push(plan.draft.id);
      // The draft ends with this batch: no mirror may write its row back,
      // and the 4409 that follows is our own.
      selfEndingRef.current = true;
      selfEndedDocRef.current = ydocRef.current;
      endedRef.current = true;
      cancelMirror();
      try {
        await SpecService.commitPublish(result, ids);
      } catch (err) {
        selfEndingRef.current = false;
        selfEndedDocRef.current = null;
        endedRef.current = false;
        // Nothing changed server-side: the draft lives on, keep mirroring it.
        if (editedRef.current) scheduleMirror();
        reportError('SpecStudio:publish', err);
        throw err;
      }
      renamePatchRef.current = null;
      setRenamePending(false);
      await refresh('own-publish', result.master);
      return result.master;
    },
    [userName, cancelMirror, scheduleMirror, refresh]
  );

  const submitForReview = React.useCallback(
    async (message: string) => {
      try {
        await flushRename();
        const d = (await flushMirror()) ?? draftRef.current;
        if (!d) return;
        const result = await SpecService.submitPublishRequest(d.id, message);
        if (result) {
          setDraft(result);
          draftRef.current = result;
          toastRef.current('Publish request submitted for review');
        }
      } catch (err) {
        reportError('SpecStudio:submit', err);
        toastRef.current(
          'Submit failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
          true
        );
      }
    },
    [flushRename, flushMirror]
  );

  // ─── Revert a published version (T5) ───
  const revert = React.useCallback(
    async (version: number) => {
      const m = masterRef.current;
      if (!m) return;
      try {
        const result = await SpecService.revertMaster(m.id, version, userName);
        if (result) {
          setMaster(result);
          masterRef.current = result;
          toastRef.current(`Reverted to v${version} → now v${result.version}`);
          // The published content moved under the editor. With no draft open,
          // show the new truth instead of stale blocks — for everyone.
          if (!draftRef.current && !editedRef.current) {
            const rows = result.coreContent || [];
            const rolled = (result.layers || []) as ClientLayer[];
            setInitialBlocks(rows);
            blocksRef.current = rows;
            blocksStaleRef.current = false;
            layersRef.current = rolled;
            setLayersState(rolled);
            const d = ydocRef.current;
            if (d && seededRef.current) {
              d.transact(() => {
                fillFragment(d.getXmlFragment(COLLAB_FIELD), rows);
                writeLayers(d.getMap<StoredLayer<ClientLayer>>('layers'), rolled);
              }, SEED_ORIGIN);
            }
          }
        }
      } catch (err) {
        reportError('SpecStudio:revert', err);
        toastRef.current(
          'Revert failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
          true
        );
      }
    },
    [userName]
  );

  const prStatus = (draft?.publishRequest?.status as SpecMasterDraftApi['prStatus']) ?? null;

  const saveState = deriveStudioChipState({
    status: collabDoc?.status ?? 'connecting',
    unsyncedChanges: collabDoc?.unsyncedChanges ?? 0,
    mirrorPending,
    ready: seeded && synced,
  });
  const unsaved = renamePending || mirrorPending || (collabDoc?.unsyncedChanges ?? 0) > 0;

  // Bind the canvas once the document has synced AND been seeded — and
  // KEEP it bound through a later disconnect: offline typing queues in the
  // Y.Doc and merges on reconnect, so an outage must never unbind the editor.
  const [boundDoc, setBoundDoc] = React.useState<Y.Doc | null>(null);
  React.useEffect(() => {
    if (ydoc && seeded && synced) setBoundDoc(ydoc);
  }, [ydoc, seeded, synced]);
  const discarded = !!collabDoc?.discarded;
  const boundUser = collabDoc?.user ?? null;
  const collab = React.useMemo<SpecCanvasCollab | null>(
    () =>
      ydoc && provider && boundUser && boundDoc === ydoc && !discarded
        ? { ydoc, provider, user: boundUser }
        : null,
    [ydoc, provider, boundUser, boundDoc, discarded]
  );

  // ─── View mode (SPEC_WORKFLOW §4): the PUBLISHED text, read-only ───
  // The socket stays open (invisibly) so View can say who is in the draft,
  // but every reader sees the published master and the canvas is unbound.
  const viewing = mode === 'view';
  // The draft ended for this connection (evicted, or the server refused a
  // mirror write with draft-ended): no draft until the re-read lands.
  const draftEnded = docDiscarded || endedRemotely;
  const viewBlocks = master?.coreContent || NO_BLOCKS;
  const viewLayers = (master?.layers || NO_LAYERS) as ClientLayer[];
  const currentBlocksOut = React.useCallback(
    () =>
      modeRef.current === 'view' ? masterRef.current?.coreContent || NO_BLOCKS : currentBlocks(),
    [currentBlocks]
  );
  const currentLayersOut = React.useCallback(
    () =>
      modeRef.current === 'view'
        ? ((masterRef.current?.layers || NO_LAYERS) as ClientLayer[])
        : layersRef.current,
    []
  );
  const setLayersOut = React.useCallback(
    (next: ClientLayer[], o?: { dirty?: boolean }) => {
      if (modeRef.current !== 'view') {
        setLayers(next, o);
        return;
      }
      // View never edits. An ALREADY-persisted layer change (an update the
      // server applied to the published master) is mirrored into the view.
      if (o?.dirty !== false) return;
      const m = masterRef.current;
      if (m) {
        const updated = { ...m, layers: next };
        masterRef.current = updated;
        setMaster(updated);
      }
      setLayers(next, o);
    },
    [setLayers]
  );

  const applyLayerDecision = React.useCallback(
    (layerId: string, apply: (layer: ClientLayer) => ClientLayer) => {
      const m = masterRef.current;
      const t = layerDecisionTargets({
        publishedLayers: (m?.layers || []) as ClientLayer[],
        draftLayers: layersRef.current,
        hasDraft: !!draftRef.current || editedRef.current,
        layerId,
        apply,
      });
      if (m) {
        const updated = { ...m, layers: t.published };
        masterRef.current = updated;
        setMaster(updated);
      }
      setLayers(t.draft, { dirty: t.draftWrite === 'edit' });
    },
    [setLayers]
  );

  return {
    loadState,
    error,
    master,
    draft,
    initialBlocks: viewing ? viewBlocks : initialBlocks,
    layers: viewing ? viewLayers : layers,
    setLayers: setLayersOut,
    applyLayerDecision,
    currentLayers: currentLayersOut,
    dirty: edited || renamePending,
    unsaved,
    saving: saveState === 'saving',
    saveState,
    liveBlocked: collabDoc?.blocked ?? null,
    peers: collabDoc?.peers ?? [],
    collab: viewing ? null : collab,
    discardedElsewhere:
      (!!collabDoc?.discarded &&
        !collabDoc.reset &&
        selfEndedDocRef.current !== (collabDoc?.ydoc ?? null)) ||
      (endedRemotely && !selfEndingRef.current),
    resetElsewhere: !!collabDoc?.reset && selfEndedDocRef.current !== (collabDoc?.ydoc ?? null),
    draftExists: draftExistsNow({ ended: draftEnded, draftRow: !!draft, edited }),
    draftInProgress: showDraftInProgress({
      ended: draftEnded,
      draftRow: !!draft,
      peerCount: collabDoc?.peers.length ?? 0,
    }),
    prStatus,
    onBlocksChanged,
    currentBlocks: currentBlocksOut,
    save,
    saveAs,
    rename,
    discard,
    publish,
    commitPublish,
    replanPublish,
    submitForReview,
    revert,
    reload: load,
    refresh,
  };
}
